import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Eye, ArrowUpRight } from 'lucide-react';
import PrivateBookingBadge from '../../shared/PrivateBookingBadge';
import { isPrivateBooking } from '../../../utils/bookingSource';

const RecentBookings = ({ bookings = [] }) => {
 const navigate = useNavigate();

 // Helper function to format currency
 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined || amount === '') return '$0.00';
 const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
 if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
 return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
 };

 // Helper function to format date
 const formatDate = (dateString) => {
 if (!dateString) return 'N/A';
 return new Date(dateString).toLocaleDateString('en-US', {
 month: 'short',
 day: 'numeric',
 year: 'numeric'
 });
 };

 // Get status color
 const getStatusColor = (status) => {
 switch (status) {
 case 'completed':
 return 'bg-emerald-100 text-emerald-800';
 case 'confirmed':
 return 'bg-surface-muted text-ink';
 case 'pending':
 return 'bg-amber-100 text-amber-800';
 case 'cancelled':
 return 'bg-red-100 text-red-800';
 default:
 return 'bg-gray-100 text-gray-800';
 }
 };

 return (
 <div className="lg:col-span-2 product-panel overflow-hidden">
 <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 sm:pt-5 pb-3">
 <div>
 <h2 className="text-base font-semibold text-ink">Recent bookings</h2>
 <p className="text-xs text-ink-tertiary mt-0.5">Latest marketplace activity</p>
 </div>
 <button
 type="button"
 onClick={() => navigate('/admin/bookings')}
 className="text-sm text-ink hover:text-ink font-medium inline-flex items-center gap-1 shrink-0"
 >
 View all
 <ArrowUpRight className="w-4 h-4" />
 </button>
 </div>
 {bookings.length > 0 ? (
 <div className="divide-y divide-gray-100">
 {bookings.map((booking) => (
 <div
 key={booking.id}
 role="button"
 tabIndex={0}
 onClick={() => navigate(`/admin/bookings/${booking.id}`)}
 onKeyDown={(e) => {
 if (e.key === 'Enter' || e.key === ' ') {
 e.preventDefault();
 navigate(`/admin/bookings/${booking.id}`);
 }
 }}
 className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 hover:bg-surface-muted/80 cursor-pointer transition-colors"
 >
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1 flex-wrap">
 <p className="text-sm font-semibold text-ink truncate">
 {booking.booking_reference || `#${String(booking.id).substring(0, 8)}`}
 </p>
 {isPrivateBooking(booking) && <PrivateBookingBadge />}
 <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(booking.status)}`}>
 {String(booking.status || '').replace(/_/g, ' ')}
 </span>
 </div>
 <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-tertiary">
 {booking.client && <span className="truncate">{booking.client.name}</span>}
 {booking.event_date && (
 <span className="inline-flex items-center gap-1">
 <Calendar className="w-3 h-3" />
 {formatDate(booking.event_date)}
 </span>
 )}
 {booking.subtotal_amount != null && (
 <span className="font-semibold text-ink tabular-nums">
 {formatCurrency(booking.subtotal_amount)}
 </span>
 )}
 </div>
 </div>
 <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" />
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-14 px-6">
 <Calendar className="w-9 h-9 text-gray-300 mx-auto mb-3" />
 <p className="text-sm font-medium text-ink">No recent bookings</p>
 <p className="text-xs text-ink-tertiary mt-1">New bookings will appear here.</p>
 </div>
 )}
 </div>
 );
};

export default RecentBookings;
